import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, ChevronDown } from "lucide-react";
import { collections } from "../data/site";
import { MaskedLines } from "./Reveal";

// Choose slides with strong lifestyle photography from catalogue
const HERO_SLIDES = [
  {
    slug: "showroom",
    eyebrow: "SeraPark Showroom",
    title: ["Yeniliklerin", "Öncüsüyüz."],
    subtitle:
      "Şanlıurfa’nın en kapsamlı seramik ve porselen showroomuna tüm misafirlerimizi bekliyoruz.",
    image: "/hero-slide-1.jpg",
    imageMobile: "/hero-slide-1-mobile.jpg",
    duration: 8500,
  },
  {
    slug: "calacatta",
    eyebrow: "02 — Koleksiyon",
    title: ["Zarif damar", "detayları."],
    subtitle:
      "Calacatta koleksiyonu — beyaz zemin üzerine ince damar detaylarıyla salon zeminlerinin klasiği.",
    image: "/hero-slide-2.jpg",
    imageMobile: "/hero-slide-2-mobile.jpg",
  },
  {
    slug: "riva",
    eyebrow: "03 — Dış Mekan",
    title: ["Doğal formda", "dayanıklı estetik."],
    subtitle:
      "20 mm dış mekân porseleni Riva — donma-çözünme direnci ve mat karakterle terasınıza uzun ömür.",
    image: "/hero-slide-3.jpg",
    imageMobile: "/hero-slide-3-mobile.jpg",
  },
];

export default function Hero() {
  const [i, setI] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setI((v) => (v + 1) % HERO_SLIDES.length),
      HERO_SLIDES[i].duration || 6500
    );
    return () => clearInterval(id);
  }, [i]);

  useEffect(() => {
    const onS = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onS, { passive: true });
    return () => window.removeEventListener("scroll", onS);
  }, []);

  const active = HERO_SLIDES[i];
  const staticSlide = HERO_SLIDES[0];
  const activeCol = collections.find((c) => c.slug === active.slug);

  return (
    <section
      data-testid="hero"
      className="relative h-[100svh] min-h-[720px] w-full overflow-hidden bg-[#0e0e0e] text-white"
    >
      {/* Background image with parallax */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active.slug}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1 }}
          transition={{ duration: 1.6, ease: [0.2, 0.7, 0.2, 1] }}
          className="absolute inset-0"
          style={{ transform: `translate3d(0, ${scrollY * 0.15}px, 0)` }}
        >
          <img
            src={active.image || activeCol?.hero}
            alt={activeCol?.name || "SeraPark"}
            className="hidden md:block w-full h-full object-cover"
          />
          <img
            src={active.imageMobile || active.image || activeCol?.hero}
            alt={activeCol?.name || "SeraPark"}
            className="md:hidden w-full h-full object-cover"
          />
          {/* Editorial dark gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0e0e0e]/60 via-[#0e0e0e]/15 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e0e]/40 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Grain */}
      <div className="grain absolute inset-0 pointer-events-none" />

      {/* Vertical index line right */}
      <div className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col items-end gap-4 z-10">
        {HERO_SLIDES.map((s, idx) => (
          <button
            key={s.slug}
            onClick={() => setI(idx)}
            data-testid={`hero-dot-${idx}`}
            className="flex items-center gap-3 group"
          >
            <span
              className={`text-[10px] tracking-[0.3em] transition-opacity ${
                idx === i ? "opacity-100" : "opacity-40"
              }`}
            >
              0{idx + 1}
            </span>
            <span
              className={`h-[1px] transition-all ${
                idx === i ? "w-14 bg-[#B8860B]" : "w-8 bg-white/40"
              }`}
            />
          </button>
        ))}
      </div>

      {/* Content */}
      <div className={`relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:block ${i < 2 ? "justify-between" : "justify-end"} md:justify-end pt-28 md:pt-0 pb-8 md:pb-28`}>
        <div className="max-w-xl md:absolute md:bottom-28 md:left-6 lg:left-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#B8860B] mb-4"
          >
            <span className="w-6 h-[1px] bg-[#B8860B]" />
            {staticSlide.eyebrow}
          </motion.div>

          <div>
            <MaskedLines
              as="h1"
              lines={staticSlide.title}
              lineClassName="text-white font-serif-display text-[26px] sm:text-[32px] md:text-[40px] leading-[1.1] tracking-[-0.01em] drop-shadow-lg"
              stagger={0.07}
              duration={0.8}
            />
          </div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-4 text-white/80 text-[13px] md:text-[14px] leading-relaxed max-w-md drop-shadow"
          >
            {staticSlide.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="hidden md:block mt-6"
          >
            <Link
              to="/urunler"
              data-testid="hero-cta-products"
              className="btn-gold text-[11px] px-4 py-2.5"
            >
              Ürünleri İncele <ArrowRight size={12} />
            </Link>
          </motion.div>
        </div>

        {/* Mobile bottom: CTA + scroll hint */}
        <div className="md:hidden flex flex-col items-center gap-4 pb-2">
          <Link
            to="/urunler"
            data-testid="hero-cta-products-mobile"
            className="btn-gold text-[11px] px-4 py-2.5"
          >
            Ürünleri İncele <ArrowRight size={12} />
          </Link>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="flex flex-col items-center gap-1 text-white/50"
          >
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ChevronDown size={20} className="text-[#B8860B]" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom badge — featured collection */}
      {activeCol && (
        <div className="hidden md:flex absolute bottom-6 left-6 md:left-10 z-10 items-center gap-3">
          <div className="h-[1px] w-8 bg-white/30" />
          <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-white/60">
            <span>Koleksiyon</span>
            <Link
              to={`/urunler/${active.slug}`}
              className="text-white/90 hover:text-[#B8860B] transition-colors flex items-center gap-1"
              data-testid="hero-featured-link"
            >
              {activeCol?.name} <ChevronRight size={10} />
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}
